import { z } from 'zod';

/**
 * Operator saved views (tracker #154 named regions + #155 home-view server sync
 * + #187 seat role-profiles).
 *
 * The shared contract for `GET/POST/DELETE /api/saved-views`, persisted in the
 * `operator_saved_views` table (migration 0061; kind='profile' admitted by
 * 0066). Three saved-view kinds share one table:
 *   - 'home'    — a single look-straight-down camera vantage {lat, lon, heightM}
 *     (lifts the #149 localStorage home view onto the server so it follows the
 *     operator across devices).
 *   - 'region'  — a named geographic bbox {west, south, east, north} the operator
 *     flies to from the command-bar VIEW group and scopes a SITREP to.
 *   - 'profile' — a named seat role-profile (#187): layer-preference DELTAS over
 *     the locked default view + a panel-arrangement snapshot, so an operator's
 *     screens come up configured for their IMS seat. Per-operator preference
 *     only — NOT an auth model.
 *
 * The DB stores the geometry/bundle in a single jsonb `view` column; these
 * schemas are the validation boundary for what may go in / come out.
 */

/** A look-straight-down camera vantage — the home view. Mirrors the web client's
 *  `CameraView` (cameraFly.ts) but lives here as the wire contract. */
export const SavedVantageSchema = z.object({
  lat: z.number().finite().min(-90).max(90),
  lon: z.number().finite().min(-180).max(180),
  heightM: z.number().finite().positive(),
});
export type SavedVantage = z.infer<typeof SavedVantageSchema>;

/** A geographic bounding box — a named region. north>south & east>west (Canada
 *  never crosses the antimeridian, so a simple ordering check is safe). */
export const SavedBboxSchema = z
  .object({
    west: z.number().finite().min(-180).max(180),
    south: z.number().finite().min(-90).max(90),
    east: z.number().finite().min(-180).max(180),
    north: z.number().finite().min(-90).max(90),
  })
  .refine((b) => b.east > b.west && b.north > b.south, {
    message: 'bbox must have east>west and north>south',
  });
export type SavedBbox = z.infer<typeof SavedBboxSchema>;

/** Max length of a region label (matches the input cap on the client). */
export const SAVED_REGION_LABEL_MAX = 80;

/** Max length of a seat-profile label (mirrors the region cap). */
export const SAVED_PROFILE_LABEL_MAX = 80;

/** A pinned feed inside a profile's panel arrangement — mirrors the web client's
 *  PinnedFeed (pinnedFeeds.ts) but lives here as the wire contract. */
export const ProfilePinnedFeedSchema = z.object({
  kind: z.enum(['camera', 'news', 'drone']),
  id: z.string().min(1).max(256),
  label: z.string().max(256),
});
export type ProfilePinnedFeed = z.infer<typeof ProfilePinnedFeedSchema>;

/** A seat role-profile bundle (#187). `layerDeltas` holds ONLY the layer keys
 *  that differ from the locked default view (defaultLayerOn) — never an absolute
 *  set — so a stored profile can't fossilize the #202 default-view baseline.
 *  Unknown layer keys are tolerated here and filtered by the client on apply
 *  (forward-compatible, same stance as the layer-preferences PUT). */
export const SavedProfileViewSchema = z.object({
  layerDeltas: z.record(z.boolean()),
  panelArrangement: z
    .object({
      pinnedFeeds: z.array(ProfilePinnedFeedSchema).max(64),
    })
    .optional(),
});
export type SavedProfileView = z.infer<typeof SavedProfileViewSchema>;

/** A saved region as returned to the client. */
export interface SavedRegion {
  id: string;
  label: string;
  bbox: SavedBbox;
  created_at: string;
}

/** A saved (custom) seat profile as returned to the client. The 3 curated seeds
 *  are code constants (seat-profiles.ts), not rows, so they never appear here. */
export interface SavedSeatProfile {
  id: string;
  label: string;
  view: SavedProfileView;
  created_at: string;
}

/** GET /api/saved-views response: the operator's home vantage (if set) + their
 *  named regions + their custom seat profiles, newest first. */
export interface SavedViewsResponse {
  home: SavedVantage | null;
  regions: SavedRegion[];
  profiles: SavedSeatProfile[];
}

/** POST /api/saved-views — set/replace the single home view. */
export const SaveHomeViewRequestSchema = z.object({
  kind: z.literal('home'),
  view: SavedVantageSchema,
});

/** POST /api/saved-views — save (or replace by same label) a named region. */
export const SaveRegionViewRequestSchema = z.object({
  kind: z.literal('region'),
  label: z.string().trim().min(1).max(SAVED_REGION_LABEL_MAX),
  bbox: SavedBboxSchema,
});

/** POST /api/saved-views — save (or replace by same label) a seat profile (#187). */
export const SaveProfileViewRequestSchema = z.object({
  kind: z.literal('profile'),
  label: z.string().trim().min(1).max(SAVED_PROFILE_LABEL_MAX),
  view: SavedProfileViewSchema,
});

/** POST body — discriminated on `kind`. */
export const SaveViewRequestSchema = z.discriminatedUnion('kind', [
  SaveHomeViewRequestSchema,
  SaveRegionViewRequestSchema,
  SaveProfileViewRequestSchema,
]);
export type SaveViewRequest = z.infer<typeof SaveViewRequestSchema>;
